import { Box, Checkbox, IconButton, Skeleton, Typography } from '@mui/material'
import Grid from '@mui/material/Grid2'
import { IMedia } from '@/interfaces'
import { MediaCard } from '@/components/medias/MediaCard'
import React, { useMemo, useState } from 'react'
import { useOne, useUpdateMany } from '@refinedev/core'
import DeleteIcon from '@mui/icons-material/Delete'
import { CustomCheckbox } from '@/components/override'
import { RadioButtonUnchecked, RadioButtonChecked } from '@mui/icons-material'
import { useFormContext } from 'react-hook-form'
import NavigateNextIcon from '@mui/icons-material/NavigateNext'
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore'
import { getCompressedUrl } from '@/utils/formattingFunctions'

interface MediaListProps {
  enabledActions: Record<"archive" | "view", boolean>;
  allowMultiple?: boolean;
  onRefetchReady?: (refetch: () => void) => void;
  onSelectionChange?: (medias: IMedia[]) => void;
  formFieldName?: string;
  currentMediaItems?: IMedia[];
}

const MediaListComponent = ({ 
  enabledActions,
  allowMultiple = true,
  onRefetchReady,
  onSelectionChange,
  formFieldName,
  currentMediaItems = []
}: MediaListProps) => {
  
  const { mutate: updateMutate } = useUpdateMany({
    resource: "medias",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const formContext = useFormContext();

  // Use useList hook instead of useTable
  const { data, isLoading, refetch } = useOne<{
      current_page: number; 
      list: IMedia[]; 
      results_per_page: number; 
      total_pages: number; 
      total_results: number
  }>({
    resource: `medias?page=${currentPage}`,
    id: "",
    queryOptions: {
      queryKey: ["medias", currentPage],
    }
  });

  // Expose refetch function to parent
  React.useEffect(() => {
    onRefetchReady?.(refetch);
  }, [refetch, onRefetchReady]);

  const mediaData = data?.data.list || [];
  const totalPages = data?.data.total_pages || 1;

  // Filter out media that's already in the form
  const filteredMedia = useMemo(() => {
    // Get current media items from form context if formFieldName is provided
    const formMediaItems = formFieldName ? formContext?.getValues(formFieldName) || [] : currentMediaItems;
    const formMediaIds = new Set(formMediaItems.map((media: IMedia) => media.id.toString()));
    
    // Filter out media that's already in the form
    return mediaData.filter((media) => media.resource_type !== "document" && !formMediaIds.has(media.id.toString()));
  }, [mediaData, formContext, formFieldName, currentMediaItems]);

  // Notify parent of selection changes
  React.useEffect(() => {
    if (onSelectionChange) {
      const selectedMedia = filteredMedia.filter(media => 
        selectedIds.includes(media.id.toString())
      );
      onSelectionChange(selectedMedia);
    }
  }, [selectedIds, onSelectionChange]);

  // Reset selection when dialog opens/closes
  React.useEffect(() => {
    setSelectedIds([]);
  }, [open]);

  // Selection handlers
  const toggleSelection = (id: string) => {
    setSelectedIds(prev => {
      if (allowMultiple) {
        return prev.includes(id) 
          ? prev.filter(selectedId => selectedId !== id)
          : [...prev, id];
      } else {
        return prev.includes(id) ? [] : [id];
      }
    });
  };

  const toggleAllSelection = () => {
    if (selectedIds.length === filteredMedia.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredMedia.map(media => media.id.toString()));
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <Grid container spacing={2} marginTop={2}>
        {Array.from({ length: 12 }).map((_, index) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={index}>
            <Skeleton variant="rounded" width="100%" height={272} />
          </Grid>
        ))}
      </Grid>
    );
  }

  // Empty state
  if (!mediaData.length) {
    return <Box p={2}>No media items available</Box>;
  }

  const selectedCount = selectedIds.length;

  return (
    <Box p={2}>
      {/* Selection header */}
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={2}
        sx={{ 
          bgcolor: (selectedCount > 0) ? 'action.selected' : 'transparent',
          p: 2,
          borderRadius: 1,
          transition: 'background-color 0.2s'
        }}
      >
        {allowMultiple && (
          <Box display="flex" gap={1} alignItems="center">
            <CustomCheckbox
              indeterminate={selectedCount > 0 && selectedCount < filteredMedia.length}
              checked={selectedCount === filteredMedia.length}
              onChange={toggleAllSelection}
            />
            {(selectedCount > 0) ? (
              <Typography variant="body1">
                {selectedCount} {selectedCount === 1 ? 'image' : 'images'} selected
              </Typography>
            ) : (
              <Typography variant="body1">
                {filteredMedia.length} {filteredMedia.length === 1 ? 'image' : 'images'} available
              </Typography>
            )}
          </Box>
        )}
        
        {(selectedCount > 0) && enabledActions.archive && (
          <IconButton
            onClick={() => {
              updateMutate({
                ids: selectedIds,
                values: { status: 'archived' }
              }, {
                onSuccess: () => {
                  setSelectedIds([]);
                  refetch();
                }
              });
            }}
            color="error"
            sx={{ borderRadius: '8px' }}
          >
            <DeleteIcon />
          </IconButton>
        )}
      </Box>

      {/* Media grid */}
      <Grid container spacing={2}>
        {filteredMedia.map((media) => (
          <Grid 
            size={{ xs: 12, sm: 6, md: 4, lg: 3 }} 
            key={media.id}
          >
            <Box 
              sx={{ 
                position: 'relative',
                cursor: 'pointer'
              }}
              onClick={(e) => {
                e.preventDefault();
                setTimeout(() => {
                  toggleSelection(media.id.toString());
                }, 0);
              }}
            >
              <Box sx={{ position: 'absolute', top: 8, left: 8, zIndex: 1 }}>
                {allowMultiple ? (
                  <CustomCheckbox
                    checked={selectedIds.includes(media.id.toString())}
                    onChange={(e) => {
                      e.stopPropagation();
                      setTimeout(() => {
                        toggleSelection(media.id.toString());
                      }, 0);
                    }}
                  />
                ) : (
                  <Checkbox
                    checked={selectedIds.includes(media.id.toString())}
                    onChange={(e) => {
                      e.stopPropagation();
                      setTimeout(() => {
                        toggleSelection(media.id.toString());
                      }, 0);
                    }}
                    icon={<RadioButtonUnchecked />}
                    checkedIcon={<RadioButtonChecked />}
                  />
                )}
              </Box>
              <MediaCard 
                id={media.id} 
                filename={media.filename} 
                cdn_url={getCompressedUrl(media.cdn_url)} 
                type={media.type} 
                enabledActions={enabledActions}
              />
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Pagination controls */}
      {totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mt: 3,
            gap: 1
          }}
        >
          <IconButton 
            onClick={() => setCurrentPage(prev => prev - 1)}
            disabled={currentPage === 1}
          >
            <NavigateBeforeIcon />
          </IconButton>
          <Typography 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              mx: 2 
            }}
          >
            Page {currentPage} of {totalPages}
          </Typography>
          <IconButton
            onClick={() => setCurrentPage(prev => prev + 1)}
            disabled={currentPage === totalPages}
          >
            <NavigateNextIcon />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export const MediaList = React.memo(MediaListComponent);